import axios from "axios";

let logoutCallback: (() => Promise<void>) | null = null;
let tokenReadyResolve: (() => void) | null = null;
let tokenReadyPromise: Promise<void> | null = null;
let isTokenReady = false;

// Create initial promise
const resetTokenPromise = () => {
  tokenReadyPromise = new Promise((resolve) => {
    tokenReadyResolve = resolve;
  });
  isTokenReady = false;
};

// Initialize the promise
resetTokenPromise();

const instance = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/accounts/${process.env.REACT_APP_ACCOUNT_ID}`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to wait for token before making requests
instance.interceptors.request.use(
  async (config) => {
    // Skip token wait for specific endpoints (like Qlik token)
    const skipTokenWait = config.url?.includes("/users/qlik-token");

    if (!skipTokenWait && !isTokenReady && tokenReadyPromise) {
      console.log("⏳ Waiting for token to be ready before making request...");
      // Wait for token to be ready with 30 second timeout
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Token wait timeout")), 30000),
      );

      try {
        await Promise.race([tokenReadyPromise, timeoutPromise]);
        console.log("✅ Token is ready, proceeding with request");
      } catch (error) {
        console.error("❌ Token wait timeout - proceeding anyway", error);
      }
    }

    // Log the actual Authorization header being sent (for debugging)
    if (config.headers?.Authorization) {
      const authHeader = config.headers.Authorization as string;
      console.log("🔐 Request being sent with Authorization header:", {
        url: config.url,
        method: config.method?.toUpperCase(),
        hasBearer: authHeader.startsWith("Bearer "),
        headerLength: authHeader.length,
        headerPreview: authHeader.substring(0, 30) + "...",
      });
    } else if (!skipTokenWait) {
      console.warn("⚠️ No Authorization header found in request:", {
        url: config.url,
        method: config.method?.toUpperCase(),
        isTokenReady,
        headers: Object.keys(config.headers || {}),
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle 401 errors
instance.interceptors.response.use(
  (response) => {
    // Return successful responses as-is
    return response;
  },
  async (error) => {
    // Log detailed error information for debugging
    if (axios.isAxiosError(error)) {
      console.error("❌ Axios Error:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        errorMessage: error.response?.data?.message || error.message,
        errorData: error.response?.data,
        sentHeaders: {
          Authorization: error.config?.headers?.Authorization
            ? `${String(error.config.headers.Authorization).substring(0, 30)}...`
            : "MISSING",
          "Content-Type": error.config?.headers?.["Content-Type"],
        },
      });
    }

    // Check if it's a 401 Unauthorized error
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      // Limpiar cache de React Query si está disponible
      if (window.queryClient) {
        window.queryClient.clear();
      }

      // Execute logout callback if available
      if (logoutCallback) {
        try {
          await logoutCallback();
        } catch (logoutError) {
          console.error("Error during automatic logout:", logoutError);
          // Fallback: forzar recarga de página
          window.location.reload();
        }
      } else {
        // Fallback: forzar recarga de página
        window.location.reload();
      }
    }

    // Always reject the promise to let the original error handling continue
    return Promise.reject(error);
  },
);

const setBaseURL = (url: string): void => {
  instance.defaults.baseURL = url;
};

const setToken = (token: string): void => {
  // Validate token before setting
  if (!token || typeof token !== "string" || token.trim() === "") {
    console.error("❌ Invalid token provided to setToken:", {
      token: token ? "[REDACTED]" : "null/undefined",
      type: typeof token,
      length: token?.length || 0,
    });
    throw new Error("Invalid token: Token must be a non-empty string");
  }

  // Check if token looks like a JWT (has 3 parts separated by dots)
  const tokenParts = token.split(".");
  if (tokenParts.length !== 3) {
    console.error("❌ Token does not appear to be a valid JWT:", {
      parts: tokenParts.length,
      expected: 3,
    });
    throw new Error("Invalid token format: Expected JWT format (3 parts)");
  }

  instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;

  console.log("✅ Token set successfully", {
    tokenLength: token.length,
    tokenPreview: `${token.substring(0, 20)}...`,
    isExpired: isTokenExpired(token),
  });

  // Mark token as ready and resolve the promise
  if (!isTokenReady) {
    isTokenReady = true;
    if (tokenReadyResolve) {
      tokenReadyResolve();
    }
  }
};

// Helper function to check if token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const tokenParts = token.split(".");
    if (tokenParts.length !== 3) return true;

    const tokenPayload = JSON.parse(atob(tokenParts[1]));
    const expireDate = new Date(tokenPayload.exp * 1000);
    const currentTime = new Date().getTime();
    const timeUntilExpiration = expireDate.getTime() - currentTime;

    return timeUntilExpiration < 0;
  } catch (error) {
    return true;
  }
};

const setLogoutCallback = (callback: () => Promise<void>): void => {
  logoutCallback = callback;
};

const markTokenNotReady = (): void => {
  resetTokenPromise();
};

const getTokenReadyStatus = (): boolean => {
  return isTokenReady;
};

const transformAxiosError = (error: Error) => {
  if (axios.isAxiosError(error)) {
    return {
      message:
        error.response?.data?.error.errorMessage ||
        error.response?.data?.message ||
        error.message,
      error: error.response?.data?.error || "Unknown error",
    };
  }
  return error;
};

const isAxiosAuthorized = (): boolean => {
  return !!instance.defaults.headers.common["Authorization"];
};

export {
  instance as axios,
  setBaseURL,
  setToken,
  setLogoutCallback,
  markTokenNotReady,
  getTokenReadyStatus,
  transformAxiosError,
  isAxiosAuthorized,
};
